import axios from 'axios';
import { pick } from 'lodash';

import route from '@H/route';
import router from '@R';

import { getErrorFromResponse } from '@H/error-codes';

import {
    verify as sharedVerify,
    getUser as sharedGetUser,
    complete as sharedComplete,
    loadPaymentConfirmation as sharedLoadPaymentConfirmation
} from '../../shared';

export default {

    /**
     * Generic step processing for marketing steps
     * @param {Function} commit Commit a mutation
     * @param {Function} dispatch Dispatch an action
     * @param {Object} options The options to pass to POST req
     * @param {String} to The route to navigate to
     */
    async processStep({ commit, dispatch }, { options, to }) {
        const { $gettext } = this._vm;

        try {
            commit('setFormSubmitting', true, { root: true });

            const { data: { user } } = await axios.post(route('marketing.enrol'), options);

            dispatch('setUser', user);

            router.push({
                name: to
            });
        } catch (err) {
            commit('setFormSubmitting', false, { root: true });
            const defaultMessage = $gettext(
                'There was a problem processing your request, please check your details and try again.'
            );
            dispatch(
                'Flash/setFlash',
                { message: getErrorFromResponse(err, defaultMessage) },
                { root: true }
            );
        }

        // Handle setting the form submitting back to false in the guard to make sure
        // that the submit button on each step stays disabled for long enough.
    },

    /**
     * Step 1 of the marketing journey.
     * @param {Object} state Current module state
     * @param {Function} dispatch Dispatch an action
     * @param {Object} rootState The root state of the application
     */
    async step1({ state, dispatch, rootState }) {

        let options = {
            ...state.user,
            ...pick(
                state,
                [
                    'first_name',
                    'last_name',
                    'email',
                    'accepted_terms',
                    'salutation',
                    'date_of_birth',//!state.submitted ? 'date_of_birth' : false,
                ].filter(Boolean)
            ),
            stage: 'register',
            next_stage: 'personal',
            language: this._vm.$language.current.substring(0, 2),
        };

        if (!state.has_password) {
            options = {
                ...options,
                ...pick(
                    rootState.Auth,
                    [
                        'password',
                        'password_confirmation'
                    ]
                ),
            }
        }

        dispatch('processStep', { options, to: 'Marketing Step 2' });
    },

    /**
     * Step 2 of the marketing journey.
     * @param {Object} state Current module state
     * @param {Function} dispatch Dispatch an action
     */
    async step2({ state, dispatch }) {

        const options = {
            ...state.user,
            ...pick(
                state,
                [
                    state.experience.length ? 'experience' : false,
                    'mobile',
                    'country_of_residence',
                ].filter(Boolean)
            ),
            // DEVTODO @luke Stage will need updating
            stage: 'personal',
            next_stage: 'address'
        }

        dispatch('processStep', { options, to: 'Marketing Step 3' });
    },

    /**
     * Step 3 of the marketing journey.
     * @param {Object} state Current module state
     * @param {Function} dispatch Dispatch an action
     */
    async step3({ state, dispatch }) {

        const options = {
            ...state.user,
            addon_price: state.addon.price,
            addon_currency: state.addon.currency,
            // DEVTODO @luke Stage will need updating
            stage: 'address',
            next_stage: 'complete'
        }

        dispatch('processStep', { options, to: 'Marketing Complete' });
    },

    /**
     * Handles fetching a single-use token and sending the user off to FX blue with the passed token/user_id
     * @param {Object} ctx - Context set by vuex (store)
     */
    async complete(ctx) {
        await sharedComplete('Marketing', ctx);
    },

    /**
     * Load payment confirmation data
     * @param {Object} ctx - Context set by vuex (store)
     */
    async loadPaymentConfirmation(ctx) {
        const { $gettext } = this._vm;
        await sharedLoadPaymentConfirmation('Marketing', $gettext, ctx);
    },

    /**
     * Complete a marketing enrolment
     * @param {Object} ctx - Context set by vuex (store)
     */
    async completeMarketingEnrolment(ctx) {
        let { $gettext } = this._vm;
        axios
            .get(route('marketing.complete'))
            .then(res => {
                ctx.commit('payment', res.data.payment);
            })
            .catch(err => {
                ctx.dispatch(
                    'Flash/setFlash',
                    $gettext('There was a problem loading subscription data.'),
                    { root: true }
                );
            });
    },

    /**
    * Verifies that a user has verified their email via the email link
    * @param {Object} ctx - Context set by vuex (store)
    */
    async emailVerify(ctx) {
        const { $gettext } = this._vm;
        await sharedVerify('marketing', $gettext, ctx);
    },

    /**
     * Checks if a user is logged in server-side
     * @param  ctx
     */
    async getUser(ctx) {
        const user = await sharedGetUser('Marketing', ctx);

        // If the user has accepted terms
        // it means they have been past step 1
        // so we want to lock down password fields
        // and terms.
        if (user.accepted_terms) {
            ctx.commit('hasSubmitted', true);
        }

        return user;
    },

    /**
     * Set the current user data for marketing enrolment.
     * @param {Function} commit Commit a mutation
     */
    setUser({ commit }, user) {
        [
            'email',
            'accepted_terms',
            'first_name',
            'last_name',
            'salutation'
        ].forEach(key => {
            commit(key, user[key]);
        });

        commit('setDateOfBirth', user.date_of_birth);
        commit('mergeUser', user);
    },

    setField(ctx, payload) {
        ctx.commit(payload.identifier, payload.data);
    },
}



// WEBPACK FOOTER //
// ./resources/assets/js/vuex/modules/Marketing/actions.js